import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { t } from '../i18n.js';
import Gebruikersmenu from './Gebruikersmenu.jsx';
import Versieknop from './Versieknop.jsx';
import Scoreknop from './Scoreknop.jsx';
import Zoeker from './Zoeker.jsx';
import { groepenVoor, useMenuTellers } from '../menu.js';
import { ICOON } from './iconen.jsx';
import logo from '../assets/adts-logo-wit.png';

const KLANT_KEY = 'adts-zero-klant';
/**
 * Instellingen en Beheer zijn geen scherm naast je werk maar een andere stand van het scherm, zoals
 * in ADTS-ONE: ze nemen het hele venster over, kop en menu incluis, en één knop brengt je terug
 * naar waar je was. Als gewoon scherm in het menu lazen ze als nog een onderdeel van ZERO.
 */
const SYSTEEM = {
  '/instellingen': { titel: 'Instellingen', icoon: 'schuif',
    wat: 'Alleen voor jou. Ze gaan met je mee naar elke computer waarop je je aanmeldt. Wat voor iedereen geldt, staat onder Beheer.' },
  '/beheer': { titel: 'Beheer', icoon: 'tandwiel',
    wat: 'Wie mag wat in ADTS ZERO, en bij welke klanten. Elke wijziging staat in het logboek.' },
};

const leesKlant = () => { try { return localStorage.getItem(KLANT_KEY) || 'alle'; } catch { return 'alle'; } };

/**
 * De schil, **volgens de standaard van ADTS-ONE** (Schil.jsx daar): een donkere bovenbalk met het merk, een
 * zoekbalk over alles en wie je bent, en daaronder een menubalk met een knop "Alle objecten" (de plattegrond)
 * naast de domeingroepen. Een groep is geen uitklapmenu maar een eigen pagina met zijn items als tegels
 * (pages/Groep.jsx, data in menu.js). ZERO's eigen zaken staan rechtsboven: welke klant je bekijkt, de
 * beveiligingsscore, de versie en het gebruikersmenu (met klant, SOC-schermen, thema en taal).
 *
 * **De klantkeuze versmalt alleen.** Hij geldt per browser (een voorkeur van de kijker), en kan alleen klanten
 * tonen waar je rechten op hebt. Kies je een klant die je kwijt bent, dan valt hij terug op Alle klanten. Zie
 * docs/besluiten/klant-kiezen-en-alle-klanten.md.
 */
export default function Schil({ mij, onAfmelden, opnieuw, children }) {
  const mag = (recht) => mij.permissions.includes(recht);
  const [klant, zetKlantRuw] = useState(leesKlant);
  const geldig = klant === 'alle' || mij.tenants.some((k) => String(k.id) === String(klant));
  const gekozen = geldig ? klant : 'alle';
  const zetKlant = (v) => { zetKlantRuw(v); try { localStorage.setItem(KLANT_KEY, v); } catch { /* geen opslag */ } };
  useEffect(() => { if (!geldig) zetKlant('alle'); }, [geldig]);

  const tellers = useMenuTellers(mag, gekozen);
  const pad = useLocation().pathname;
  const naar = useNavigate();
  /* Waar je was voordat je Instellingen of Beheer opende; daar brengt de knop je terug. */
  const terug = useRef('/');
  useEffect(() => { if (!SYSTEEM[pad]) terug.current = pad; }, [pad]);
  useEffect(() => {
    if (!SYSTEEM[pad]) return undefined;
    const toets = (e) => { if (e.key === 'Escape' && !e.target.closest?.('input, textarea, select')) naar(terug.current); };
    document.addEventListener('keydown', toets);
    return () => document.removeEventListener('keydown', toets);
  }, [pad, naar]);

  /* Het paneel met alle objecten. Escape sluit het, net als een klik ernaast. */
  const [alleOpen, zetAlleOpen] = useState(false);
  useEffect(() => { zetAlleOpen(false); }, [pad]);
  useEffect(() => {
    if (!alleOpen) return undefined;
    const toets = (e) => { if (e.key === 'Escape') zetAlleOpen(false); };
    document.addEventListener('keydown', toets);
    return () => document.removeEventListener('keydown', toets);
  }, [alleOpen]);

  const systeem = SYSTEEM[pad];

  if (systeem) {
    return (
      <div className="systeemlaag">
        <div className="systeemkop">
          <span className="kopicoon">{ICOON[systeem.icoon]}</span>
          <h1>{t(systeem.titel)}</h1>
          <span className="wat">{t(systeem.wat)}</span>
          <button type="button" className="btn sluit" onClick={() => naar(terug.current)}>
            <span className="regelicoon">{ICOON.terug}</span>{t('Terug naar ADTS ZERO')}
          </button>
        </div>
        <div className="systeeminhoud"><div className="setpagina">{children(gekozen)}</div></div>
      </div>
    );
  }

  const leest = mag('alerts.lezen');
  const k = mij.tenants.find((x) => String(x.id) === String(gekozen));
  const groepen = groepenVoor(mag);
  const telVan = (g) => g.items.reduce((n, i) => n + (i.teller ? tellers[i.teller] : 0), 0);
  const hierbij = (g) => pad === `/groep/${g.id}` || g.items.some((i) => pad === i.pad || pad.startsWith(`${i.pad}/`));

  return (
    <>
      <header className="zbar">
        <span className="zbrand">
          <NavLink className="brandlink" to="/"><img className="logo" src={logo} alt="ADTS" width="74" height="22" /><span className="mark"><span>ZERO</span><span className="licht">Insight</span></span></NavLink>
          <Versieknop label />
        </span>
        {leest && <Zoeker mag={mag} klant={gekozen} tenants={mij.tenants} zetKlant={zetKlant} />}
        <div className="topright">
          {gekozen !== 'alle' && k && <span className="klantchip" title={t('Klant')}>{k.name}</span>}
          {leest && <Scoreknop haal={() => api.statusScore(gekozen)} sleutel={['status', gekozen]} naar="/status" />}
          <Gebruikersmenu mij={mij} onAfmelden={onAfmelden} opnieuw={opnieuw}
            klant={{ lijst: mij.tenants, gekozen, zet: zetKlant, soc: leest }} />
        </div>
      </header>

      <nav className="menubalk" aria-label={t('Hoofdmenu')}>
        <button type="button" className="allbtn" aria-expanded={alleOpen} onClick={() => zetAlleOpen((v) => !v)}>
          <span className="bars" aria-hidden="true"><i /><i /><i /></span>
          {t('Alle objecten')}
        </button>
        <div className="menu">
          {leest && <NavLink to="/" end className={({ isActive }) => `menu-item${isActive ? ' nu' : ''}`}>{t('Overzicht')}</NavLink>}
          {groepen.map((g) => {
            const teller = telVan(g);
            return (
              <NavLink key={g.id} to={`/groep/${g.id}`} className={`menu-item${hierbij(g) ? ' nu' : ''}`}>
                {t(g.titel)}{teller > 0 && <span className="count hot">{teller}</span>}
              </NavLink>
            );
          })}
        </div>

        {/* Alle objecten: de plattegrond onder de menubalk. Absoluut, zodat de balk eronder niet verspringt. */}
        {alleOpen && (
          <>
            <div className="backdrop" onClick={() => zetAlleOpen(false)} />
            <div className="allpanel">
              {groepen.map((g) => (
                <div className="allcol" key={g.id}>
                  <span className="label">{t(g.titel)}</span>
                  {g.items.map((i) => (
                    <button key={i.pad} type="button" className="allitem" onClick={() => { zetAlleOpen(false); naar(i.pad); }}>
                      <span>{t(i.label)}</span>
                      {i.teller && tellers[i.teller] > 0 && <span className="count hot">{tellers[i.teller]}</span>}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
      </nav>

      {mij.via === 'noodingang' && (
        <div className="nood-balk"><div>{t('Je bent aangemeld via de noodingang. Dat staat in het logboek.')}</div></div>
      )}
      <main>{children(gekozen)}</main>
    </>
  );
}
